import { VolumeOffRounded, VolumeUpRounded } from '@mui/icons-material';
import { Box, Fade, IconButton } from '@mui/material';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';

import { AnalyticsService, EVENTS, log } from '@/services';
import {
  selectIsPlayerPlaying,
  selectPersistedPlayerVolumePreference,
  useAppSelector,
} from '@/store';
import { Episode } from '@/types';

type EpisodeCoverImageTrailerPreviewProps = {
  episode: Episode;
};

export const EpisodeCoverImageTrailerPreview = ({
  episode,
}: EpisodeCoverImageTrailerPreviewProps) => {
  const isPlayerPlaying = useAppSelector(selectIsPlayerPlaying);
  const volume = useAppSelector(selectPersistedPlayerVolumePreference);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isEnded, setIsEnded] = useState(false);

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.volume = volume / 100;
  }, [volume]);

  const handleOnReady = () => {
    setIsLoaded(true);
    videoRef.current?.play();
    AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_START, {
      episodeId: episode.episodeId,
    });
  };

  const handleOnError = (event: SyntheticEvent<HTMLVideoElement>) => {
    log(
      new Error('EpisodeTrailerPreview error', {
        cause: event.currentTarget.error,
      }),
    );

    setIsEnded(true);
  };

  const handleOnEnded = () => {
    setIsEnded(true);

    AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_END, {
      episodeId: episode.episodeId,
    });
  };

  const toggleMute = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (isMuted) {
      AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_UNMUTE, {
        episodeId: episode.episodeId,
      });
    } else {
      AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.TRAILER_PREVIEW_MUTE, {
        episodeId: episode.episodeId,
      });
    }

    setIsMuted(!isMuted);
  };

  return (
    <Fade in={isLoaded && !isEnded} timeout={1000}>
      <Box sx={styles.root}>
        <Box
          component="video"
          ref={videoRef}
          src={episode.trailerPreviewUrl}
          onEnded={handleOnEnded}
          onError={handleOnError}
          onCanPlay={handleOnReady}
          disablePictureInPicture
          playsInline
          muted={isMuted || isPlayerPlaying}
          sx={styles.video}
        />
        {!isPlayerPlaying && (
          <Box sx={styles.controls}>
            <IconButton
              sx={{ p: 0 }}
              disableRipple
              aria-label="Mute"
              onClick={toggleMute}>
              {isMuted ? (
                <VolumeOffRounded sx={styles.volumeIcon} />
              ) : (
                <VolumeUpRounded sx={styles.volumeIcon} />
              )}
            </IconButton>
          </Box>
        )}
      </Box>
    </Fade>
  );
};

const styles = {
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  controls: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    display: 'flex',
    gap: 1,
  },
  volumeIcon: {
    color: 'white',
    cursor: 'pointer',
    backgroundColor: 'rgba(25, 0, 0, 0.3)',
    borderRadius: '50%',
    width: 32,
    height: 32,
    p: 0.5,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
  },
};
